import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const stats = [
  {
    value: 150,
    suffix: "+",
    label: "Metrics Analyzed",
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-9 h-9 text-blue-500" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="10" width="20" height="20" rx="3" stroke="currentColor" strokeWidth="2" />
        <rect x="14" y="14" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <line x1="15" y1="10" x2="15" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="20" y1="10" x2="20" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="25" y1="10" x2="25" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="15" y1="30" x2="15" y2="34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="20" y1="30" x2="20" y2="34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="25" y1="30" x2="25" y2="34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="10" y1="15" x2="6" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="10" y1="20" x2="6" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="10" y1="25" x2="6" y2="25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="30" y1="15" x2="34" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="30" y1="20" x2="34" y2="20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="30" y1="25" x2="34" y2="25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    value: 40,
    suffix: "%",
    label: "Projects Completed",
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-9 h-9 text-blue-500" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="12" width="28" height="20" rx="3" stroke="currentColor" strokeWidth="2" />
        <line x1="6" y1="18" x2="34" y2="18" stroke="currentColor" strokeWidth="1.8" />
        <line x1="12" y1="24" x2="20" y2="24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="12" y1="28" x2="24" y2="28" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <rect x="10" y="8" width="20" height="5" rx="2" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    value: 50,
    suffix: "+",
    label: "Happy Clients",
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-9 h-9 text-blue-500" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="14" width="28" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
        <line x1="4" y1="32" x2="36" y2="32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M17 22.5c1.66-1.5 4.34-1.5 6 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M14.5 20c2.9-2.7 8.1-2.7 11 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="20" cy="25" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    // For "94/100" we animate the numerator (94) and show "/100" as a static suffix
    value: 94,
    suffix: "/100",
    label: "Team Member Score",
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-9 h-9 text-blue-500" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="15" r="6" stroke="currentColor" strokeWidth="2" />
        <path d="M8 34c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

function useCounter(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    setCount(0);
    let startTime = null;
    let raf;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);

  return count;
}

function StatItem({ stat, index, started }) {
  const count = useCounter(stat.value, 1800, started);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 + index * 0.13 }}
      className="flex items-center gap-5"
    >
      {/* Icon box */}
      <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shrink-0 shadow-md">
        {stat.icon}
      </div>

      {/* Text */}
      <div>
        <div className="text-white font-extrabold text-[1.9rem] leading-none tracking-tight">
          {count}{stat.suffix}
        </div>
        <div className="text-blue-100 text-[0.93rem] mt-1 font-medium">{stat.label}</div>
      </div>
    </motion.div>
  );
}

export default function StatsSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="w-full py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="bg-blue-500 rounded-2xl px-8 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 items-center gap-8 lg:gap-4"
        >
          {stats.map((stat, i) => (
            <div key={i} className="flex items-center gap-0">
              <StatItem stat={stat} index={i} started={inView} />
              {/* Vertical divider — visible between items on lg */}
              {i < stats.length - 1 && (
                <div className="hidden lg:block w-px h-12 bg-blue-400 shrink-0 ml-auto" />
              )}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}